<template>
  <div class="fixed inset-0 -top-6 z-50 overflow-hidden">
    <!-- Backdrop -->
    <Transition name="fade">
      <div 
        v-show="true"
        class="absolute inset-0 bg-gray-500 bg-opacity-50 fade-in" 
        @click="$emit('close')"
      ></div>
    </Transition>
    
    <!-- Slide-over panel -->
    <Transition name="slide">
      <div 
        v-show="true"
        class="absolute inset-y-0 right-0 max-w-[800px] w-full bg-white shadow-xl slide-in"
      >
        <div class="h-full flex flex-col">
          <!-- Header -->
          <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900">Batch Edit Tasks ({{ items.length }})</h2>
            <button 
              class="p-1 rounded-md text-gray-400 hover:text-gray-500"
              @click="$emit('close')"
            >
              <X class="w-5 h-5" />
            </button>
          </div>
          
          <!-- Content -->
          <div class="flex-1 overflow-y-auto">
            <!-- Selected items list -->
            <div class="p-4 border-b border-gray-200 bg-gray-50">
              <h3 class="text-sm font-medium text-gray-700 mb-2">Selected Tasks:</h3>
              <div class="space-y-1">
                <div 
                  v-for="item in items" 
                  :key="item.id"
                  class="text-sm text-gray-600 flex items-center gap-2"
                >
                  <div class="min-w-16 text-center text-gray-400">Week {{ item.week_order }}</div>
                  <span>{{ item.task.name }}</span>
                </div>
              </div>
            </div>

            <!-- Edit form -->
            <form @submit.prevent="handleSubmit" class="p-4 space-y-6">
              <!-- Week Order -->
              <FormField 
                v-slot="{ field }" 
                :name="'week_order'" 
                :model-value="form.week_order"
                @update:model-value="(value) => updateField('week_order', value)"
              >
                <FormItem>
                  <Label>Week</Label>
                  <FormControl>
                    <Select v-bind="field">
                      <SelectTrigger class="w-full">
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Available Weeks</SelectLabel>
                          <SelectItem 
                            v-for="option in availableWeeks" 
                            :key="option"
                            :value="option"
                          >
                            Week {{ option }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Points -->
              <FormField 
                v-slot="{ field }" 
                :name="'points'" 
                :model-value="form.points"
                @update:model-value="(value) => updateField('points', value)"
              >
                <FormItem>
                  <Label>Points</Label>
                  <FormControl>
                    <Input 
                      v-bind="field"
                      type="number" 
                      min="0"
                      placeholder="No change"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Required Step -->
              <FormField 
                v-slot="{ field }" 
                :name="'is_required'" 
                :model-value="form.is_required"
                @update:model-value="(value) => updateField('is_required', value)"
              >
                <FormItem>
                  <Label>Required Step</Label>
                  <FormControl>
                    <Select v-bind="field">
                      <SelectTrigger class="w-full">
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Is this step required?</SelectLabel>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Review Required -->
              <FormField 
                v-slot="{ field }" 
                :name="'review_required'" 
                :model-value="form.review_required"
                @update:model-value="(value) => updateField('review_required', value)"
              >
                <FormItem>
                  <Label>Review Required</Label>
                  <FormControl>
                    <Select v-bind="field">
                      <SelectTrigger class="w-full">
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Does this step need review?</SelectLabel>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Submit buttons -->
              <div class="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  @click="$emit('close')"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  :loading="isSubmitting"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { X } from 'lucide-vue-next'
import type { WeeklyTaskItem } from '@/types/curriculum'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FormField, FormItem, FormControl } from '@/components/ui/form'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

const props = defineProps<{
  items: WeeklyTaskItem[]
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const isSubmitting = ref(false)

// Helper functions for handling mixed values with type safety
const getCommonValue = (items: WeeklyTaskItem[], path: string) => {
  const values = items.map(item => {
    return path.split('.').reduce((obj: any, key) => obj?.[key], item)
  })
  const uniqueValues = new Set(values)
  return uniqueValues.size === 1 ? values[0] : undefined
}

const hasCommonValue = (items: WeeklyTaskItem[], path: string) => {
  const values = items.map(item => {
    return path.split('.').reduce((obj: any, key) => obj?.[key], item)
  })
  return new Set(values).size === 1
}

interface FormState {
  week_order: number | undefined
  points: number | undefined
  is_required: string | undefined
  review_required: string | undefined
}

// Form state with proper type handling
const form = ref<FormState>({
  week_order: undefined,
  points: undefined,
  is_required: undefined,
  review_required: undefined
})

// Watch for form field updates
const updateField = (field: keyof FormState, value: any) => {
  form.value[field] = value
}

// Available weeks for selection
const availableWeeks = computed(() => {
  const maxWeek = Math.max(...props.items.map(item => item.week_order))
  return Array.from({ length: maxWeek + 2 }, (_, i) => i + 1)
})

// Handle form submission
const handleSubmit = async () => {
  try {
    isSubmitting.value = true

    // Prepare the update data, only including fields that were changed
    const updateData = {
      path_step_ids: props.items.map(item => item.id),
      updates: {} as Record<string, any>
    }

    if (form.value.week_order !== undefined) {
      updateData.updates.week_order = form.value.week_order
    }
    if (form.value.points !== undefined) {
      updateData.updates.points = form.value.points
    }
    if (form.value.is_required !== undefined) {
      updateData.updates.is_required = form.value.is_required === 'yes'
    }
    if (form.value.review_required !== undefined) {
      updateData.updates.review_required = form.value.review_required === 'yes'
    }

    // Make the API call
    await api.post(API_ROUTES.BATCH_UPDATE_PATH_STEPS, updateData)

    emit('saved')
    emit('close')
  } catch (error) {
    console.error('Failed to update path steps:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for panel */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}

/* Fade in animation */
.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slide in animation */
.slide-in {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
</style> 